/**
 * Total pasteboard work-area extent (pt), both axes. The pasteboard is centered on the spread's
 * bleed box, so this gives roughly half (15000pt) of reachable space in every direction from
 * ruler 0,0. Single source of truth for the pasteboard's document-space size —
 * `center-canvas/hooks/use-spread-layout.ts` derives its on-screen pixel size from this (× zoom
 * scale) rather than hardcoding it separately, so the rendered pasteboard and this placement-bounds
 * constant can never drift out of sync.
 *
 * Also the outer boundary an object (shape or placed photo) can never cross: a shape/photo pushed
 * past this gets reverted and the user sees a pasteboard-boundary notice (see
 * `lib/groups/pasteboard-bounds.ts`, `use-home-shape-persistence.ts`'s pasteboard-bounds guard in
 * `handleShapeCommitted`/`handleBatchShapesCommitted`). Also the default clamp bound for photo
 * drops (`getPasteboardPlacementBounds`/`clampPhotoCenterToCanvas`).
 */
export const EDITOR_PASTEBOARD_SIZE_PT = 30000

/** Points per inch — spread coordinates match print points. */
export const PHOTO_PT_PER_INCH = 72

/**
 * Legacy `IM_INI_SCALE_RATE` (odctrl.js): free-drop display size assumes 300 DPI print assets.
 * displayPt = floor(pixel × 72/300)
 */
export const PHOTO_INI_SCALE_RATE = PHOTO_PT_PER_INCH / 300

/**
 * Fallback square size (pt) when pixel dimensions are unknown.
 * Equals a 3000×3000 px source at {@link PHOTO_INI_SCALE_RATE}.
 */
export const PHOTO_PLACEMENT_DEFAULT_WIDTH_PT = 720

/** Ghost preview opacity while dragging. */
export const PHOTO_DRAG_GHOST_OPACITY = 0.6

/** Thumbnail opacity while its photo is being dragged. */
export const PHOTO_TRAY_DRAGGING_THUMB_OPACITY = 0.6

/** Pointer must move more than this (screen px) before drag starts. */
export const PHOTO_DRAG_THRESHOLD_SCREEN_PX = 4

/** Touch long-press duration before drag starts (ms). */
export const PHOTO_TOUCH_LONG_PRESS_MS = 500

/** Keyboard placement: center of spread when no pointer position. */
export const PHOTO_KEYBOARD_DEFAULT_CENTER_FRACTION = 0.5
